import { Button, Modal } from "antd";
import * as Styles from "./styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { formatDistance } from "date-fns";
import { message } from "antd";
import axios from "../../../../common/api/axios";
import OkPopup from "../ok-popup/OkPopup";
import { ReactComponent as InfoIcon } from "../icons/info-icon.svg";
import { ReactComponent as InfoIconWhite } from "../icons/info-icon-white.svg";
import { ReactComponent as HoldInfoIcon } from "./icons/hold-info.svg";
import { ReactComponent as ProgressInfoIcon } from "./icons/progress-info.svg";
import { ReactComponent as TickIcon } from "../icons/tick-icon.svg";
import { ReactComponent as ProductPlaceholder } from "./icons/product.svg";
import { formatSlotDisplay } from "../../../../common/utils/slot";
import {
  flattenStaticPickWallSlots,
  matchesStaticSlot,
  StaticPickWallSlot,
} from "../../../../common/utils/staticPickWall";

export interface OrderDetailsProps { pickItemsData?: any, pickStationsFirstData?: any, pickStationsSecondData?: any }

const COMPLETED_SLOT_FLASH_MS = 3000;

export default function DropBinDetails({ pickItemsData, pickStationsFirstData, pickStationsSecondData }: OrderDetailsProps) {
  const [qCount, setQCount] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const [completedSlots, setCompletedSlots] = useState<Record<string, any>>({});
  const [binId, setBinId] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [itemsData, setItemsData] = useState<any>([]);
  const [staticItemsData, setStaticItemsData] = useState<any>([]);
  const [staticSlots, setStaticSlots] = useState<StaticPickWallSlot[]>([]);
  const [allQty, setAllQty] = useState(0);
  const [station, setStation] = useState(0);
  const [isOkPopup, setIsOkPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [itemModalLoading, setItemModalLoading] = useState(false);

  const [firstRow, setFirstRow] = useState<any[]>(pickStationsFirstData || []);
  const [secondRow, setSecondRow] = useState<any[]>(pickStationsSecondData || []);

  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const skuDetailsCache = useRef(new Map<string, any>());
  const completedSlotTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const DROP_SLOT_STATES: Record<string, { bg: string; border: string; text: string }> = {
    default: { bg: "#e8e8e8", border: "#b5b5b5", text: "#333" },
    progress: { bg: "#d6f4ff", border: "#69c0ff", text: "#003a8c" },
    hold: { bg: "#fff3cd", border: "#ffe58f", text: "#614700" },
    completed: { bg: "#d4edda", border: "#52c41a", text: "#135200" },
  };

  const formatQuantity = (value: number): string => {
    if (Number.isNaN(value) || value === undefined || value === null) {
      return "00";
    }
    const numericValue = Math.max(0, Math.floor(value));
    return numericValue < 10 ? `0${numericValue}` : String(numericValue);
  };

  const mergeTaskUiState = useCallback((current: any[], incoming: any[]) => {
    const existingMap = new Map<string, any>();

    current.forEach((item: any) => {
      existingMap.set(item.id, item);
    });

    return incoming.map((item: any) => {
      const existing = existingMap.get(item.id);
      const formattedQty = formatQuantity(item.quantity ?? 0);

      return {
        ...item,
        orderColor: existing?.orderColor ?? "default",
        selectedQty: existing?.selectedQty ?? formattedQty,
        completedStatus: existing?.completedStatus ?? false,
        okLoader: existing?.okLoader ?? false,
      };
    });
  }, []);

  const resolveBatchId = useCallback((task: any) => {
    return (
      task?.batchId ??
      task?.replenishTask?.batchId ??
      task?.replenishTask?.binSkus?.find((sku: any) => sku?.batchId)?.batchId ??
      null
    );
  }, []);

  const getVisibleSlotItem = useCallback((slotItems: any[]) => {
    if (!Array.isArray(slotItems) || slotItems.length === 0) {
      return null;
    }

    return slotItems.find((item: any) => item.orderColor !== "completed") ?? slotItems[0];
  }, []);

  const totalOrderCount = useMemo(() => {
    const uniqueItems = new Map<string, any>();

    [...itemsData, ...staticItemsData].forEach((item: any) => {
      if (item?.id) {
        uniqueItems.set(item.id, item);
      }
    });

    Object.values(completedSlots).forEach((item: any) => {
      if (item?.id && !uniqueItems.has(item.id)) {
        uniqueItems.set(item.id, item);
      }
    });

    return Array.from(uniqueItems.values()).reduce((total: number, currentValue: any) => {
      const binSkus = Array.isArray(currentValue?.replenishTask?.binSkus)
        ? currentValue.replenishTask.binSkus
        : [];
      const qty = binSkus.reduce(
        (sum: number, sku: any) => sum + (Number(sku?.quantity) || 0),
        0
      );
      return total + qty;
    }, 0);
  }, [completedSlots, itemsData, staticItemsData]);
  //const firstRow = ["11,4,1", "9,4,1", "8,4,1"];
  useEffect(() => {
    setFirstRow(pickStationsFirstData || []);
    setSecondRow(pickStationsSecondData || []);
  }, [pickStationsFirstData, pickStationsSecondData]);

  useEffect(() => {
    let isMounted = true;

    const fetchStaticSlots = async () => {
      try {
        const res = await axios.get("/StaticPickWall", {
          headers: {
            Accept: "application/json",
            tenantId: 1,
          },
        });

        if (isMounted && res.status === 200) {
          setStaticSlots(flattenStaticPickWallSlots(res.data));
        }
      } catch (_error) {
        if (isMounted) {
          setStaticSlots([]);
        }
      }
    };

    fetchStaticSlots();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      Object.values(completedSlotTimersRef.current).forEach((timer) => {
        clearTimeout(timer);
      });
      completedSlotTimersRef.current = {};
    };
  }, []);

  const clearCompletedSlotTimer = useCallback((position: string) => {
    const timer = completedSlotTimersRef.current[position];
    if (timer) {
      clearTimeout(timer);
      delete completedSlotTimersRef.current[position];
    }
  }, []);

  const scheduleCompletedSlotClear = useCallback((position: string, taskId: string) => {
    clearCompletedSlotTimer(position);

    completedSlotTimersRef.current[position] = setTimeout(() => {
      setCompletedSlots((current) => {
        const slot = current[position];

        if (!slot || slot.id !== taskId) {
          return current;
        }

        const next = { ...current };
        delete next[position];
        return next;
      });

      delete completedSlotTimersRef.current[position];
    }, COMPLETED_SLOT_FLASH_MS);
  }, [clearCompletedSlotTimer]);

  const fetchSkuDetails = useCallback(async (skuId: string, itemId: string) => {
    if (!skuId) {
      return;
    }

    const applyDetails = (details: any) => {
      if (!details) {
        return;
      }

      const nextImage = details?.imageUri ?? details?.imageUrl ?? "";
      const nextName = details?.productName ?? "";
      const nextDescription = details?.description ?? "";
      const nextSkuId = details?.skuId ?? skuId;

      const updateItemDetails = (items: any[]) =>
        items.map((obj: any) => {
          if (obj.id !== itemId) {
            return obj;
          }

          return {
            ...obj,
            skuId: nextSkuId || obj.skuId,
            productName: nextName || obj.productName,
            productDescription: nextDescription || obj.productDescription,
            image: nextImage || obj.image,
          };
        });

      setItemsData((current: any[]) => updateItemDetails(current));
      setStaticItemsData((current: any[]) => updateItemDetails(current));

      setSelectedOrder((current: any) => {
        if (!current || current.id !== itemId) {
          return current;
        }

        return {
          ...current,
          skuId: nextSkuId || current.skuId,
          productName: nextName || current.productName,
          productDescription: nextDescription || current.productDescription,
          image: nextImage || current.image,
        };
      });
    };

    const cached = skuDetailsCache.current.get(skuId);
    if (cached) {
      applyDetails(cached);
      return;
    }

    try {
      const res = await axios.get(`/MasterData/sku/${skuId}`, {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });

      if (res.status === 200) {
        const details = res.data ?? {};
        skuDetailsCache.current.set(skuId, details);
        applyDetails(details);
      }
    } catch (error: any) {
      messageApi.open({
        type: "error",
        content: "Unable to load SKU details.",
      });
    }
  }, [messageApi]);
  const normalizedItems = useMemo(() => {
    if (!Array.isArray(pickItemsData)) {
      return [];
    }

    return pickItemsData.reduce((acc: any[], val: any) => {
      const from = val?.replenishTask?.from;
      if (!from) {
        return acc;
      }

      const orderPlace = Object.values(from);
      const joinedItem = orderPlace.join(",");

      const binSkus = Array.isArray(val?.replenishTask?.binSkus)
        ? val.replenishTask.binSkus
        : [];

      const skuQuantity = binSkus.reduce((sum: number, sku: any) => sum + (Number(sku?.quantity) || 0), 0);
      const formattedQty = formatQuantity(skuQuantity);

      const primarySku = binSkus.length > 0 ? binSkus[0] : undefined;
      const cachedDetails = val?.replenishTask?.skuId
        ? skuDetailsCache.current.get(val.replenishTask.skuId)
        : undefined;
      const skuDetails = Array.isArray(val?.skuDetails) && val.skuDetails.length > 0
        ? val.skuDetails[0]
        : cachedDetails;

      const baseSkuId = val?.replenishTask?.skuId
        ?? primarySku?.skuId
        ?? skuDetails?.skuId
        ?? val?.skuId
        ?? "";

      if (baseSkuId && skuDetails && !cachedDetails) {
        skuDetailsCache.current.set(baseSkuId, skuDetails);
      }

      acc.push({
        ...val,
        position: joinedItem,
        quantity: skuQuantity,
        formattedQty,
        skuId: baseSkuId,
        productName: val?.productName
          ?? skuDetails?.productName
          ?? primarySku?.productName
          ?? primarySku?.name
          ?? baseSkuId,
        productDescription: val?.productDescription
          ?? val?.description
          ?? skuDetails?.description
          ?? primarySku?.description
          ?? "",
        image: val?.image
          ?? val?.productImage
          ?? skuDetails?.imageUri
          ?? skuDetails?.imageUrl
          ?? primarySku?.image
          ?? primarySku?.imageUrl
          ?? "",
      });

      return acc;
    }, [] as any[]);
  }, [pickItemsData]);

  useEffect(() => {
    if (!Array.isArray(pickItemsData) || pickItemsData.length === 0) {
      setItemsData([]);
      setStaticItemsData([]);
      setSelectedOrder(null);
      return;
    }

    const nextRegularItems: any[] = [];
    const nextStaticItems: any[] = [];

    normalizedItems.forEach((item: any) => {
      const matchedStaticSlot = staticSlots.find((slot) =>
        matchesStaticSlot(slot, item?.replenishTask?.from)
      );

      if (matchedStaticSlot) {
        nextStaticItems.push({
          ...item,
          isStatic: true,
          staticSlotId: matchedStaticSlot.id,
        });
        return;
      }

      nextRegularItems.push({
        ...item,
        isStatic: false,
      });
    });

    setItemsData((current: any[]) => mergeTaskUiState(current, nextRegularItems));
    setStaticItemsData((current: any[]) => mergeTaskUiState(current, nextStaticItems));

    const nextVisibleItems = [...nextRegularItems, ...nextStaticItems];

    if (selectedOrder && !nextVisibleItems.some((item: any) => item.id === selectedOrder.id)) {
      setSelectedOrder(null);
    }

    setCompletedSlots((current) => {
      const next = { ...current };

      nextVisibleItems.forEach((item: any) => {
        const position = item?.position;
        const completedSlot = position ? next[position] : undefined;

        if (position && completedSlot && completedSlot.id !== item.id) {
          clearCompletedSlotTimer(position);
          delete next[position];
        }
      });

      return next;
    });
  }, [
    clearCompletedSlotTimer,
    mergeTaskUiState,
    normalizedItems,
    pickItemsData,
    selectedOrder,
    staticSlots,
  ]);

  const dateTimeFormat = (time: string) => {
    const distance = formatDistance(Date.now(), new Date(time), {
      addSuffix: true,
    });
    const firstWord = distance.replace(/in/g, "");
    const finalWord = firstWord.replace(/about/g, "");
    const formatted = finalWord.replace(/mutes/g, "Mins");
    const one = formatted.replace(/mute/g, "Minute");
    const two = one.replace(/ago/g, "");
    return two;
  };

  const onHandleOkItem = async (id: string) => {
    const isStaticTask =
      selectedOrder?.isStatic === true ||
      staticItemsData.some((obj: any) => obj.id === id);
    const targetItemsData = isStaticTask ? staticItemsData : itemsData;
    const setTargetItemsData = isStaticTask ? setStaticItemsData : setItemsData;
    const currentItem = targetItemsData.find((obj: any) => obj.id === id);
    const ptlTaskId = currentItem?.replenishTask?.binSkus?.[0]?.ptlTaskId;
    
    setTargetItemsData((current: any[]) =>
      current.map((obj: any) => {
        if (obj.id === id) {
          return {
            ...obj,
            okLoader: true,
          };
        }
        return obj;
      })
    );
    setSelectedOrder((current: any) => (
      current && current.id === id
        ? {
          ...current,
          okLoader: true,
        }
        : current
    ));
    //setOkayLoading(true);
    try {
      const res = await axios.put(
        `/PickStation/updateReplenishTaskQuantityAndComplete/${ptlTaskId}/${qCount}`,
        null,
        {
          headers: {
            Accept: "application/json",
            tenantId: 1,
          },
        }
      );
      if (res.status === 200) {
        const completedItem = currentItem
          ? {
            ...currentItem,
            orderColor: "completed",
            completedStatus: true,
            okLoader: false,
            selectedQty: formatQuantity(qCount),
          }
          : null;

        messageApi.success({
          content: "Item Completed!",
          className: "order",
        });
        setAllQty((prevState) => prevState + qCount);
        if (completedItem?.position) {
          setCompletedSlots((current) => ({
            ...current,
            [completedItem.position]: completedItem,
          }));
          scheduleCompletedSlotClear(completedItem.position, completedItem.id);
        }
        setTargetItemsData((current: any[]) =>
          current.map((obj: any) => {
            if (obj.id === id) {
              return {
                ...obj,
                orderColor: "completed",
                completedStatus: true,
                okLoader: false,
                selectedQty: formatQuantity(qCount),
              };
            }

            return obj;
          })
        );
        setSelectedOrder(null);
        setSelectedTaskId("");
        setSelectedDate("");
        setIsItemModalVisible(false);
        setItemModalLoading(false);
        setQCount(0);
        //setOkayLoading(false);
      }
    } catch (error: any) {
      setErrorMessage(error?.response?.data ?? "Failed to complete item");
      setTargetItemsData((current: any[]) =>
        current.map((obj: any) => {
          if (obj.id === id) {
            return {
              ...obj,
              okLoader: false,
            };
          }
          return obj;
        })
      );
      setSelectedOrder((current: any) => (
        current && current.id === id
          ? {
            ...current,
            okLoader: false,
          }
          : current
      ));
      setIsOkPopup(true);
      //setOkayLoading(false);
    }
  };

  const isOpenOkPopup = (taskId: string) => {
    onHandleOkItem(taskId);
  };

  const onHandleRestart = () => {
    window.location.reload();
  };

  const isCloseOkPopup = () => {
    setIsOkPopup(false);
  };
  const isCancelOkPopup = () => {
    setIsOkPopup(false);
  };

  const onHandleOrderItem = (item: any) => {
    if (!item || item.completedStatus) {
      return;
    }

    const isStaticTask = item?.isStatic === true;
    const setTargetItemsData = isStaticTask ? setStaticItemsData : setItemsData;

    const skuQuantity = Array.isArray(item?.replenishTask?.binSkus)
      ? item.replenishTask.binSkus.reduce((sum: number, sku: any) => sum + (Number(sku?.quantity) || 0), 0)
      : 0;

    const formattedQty = formatQuantity(skuQuantity);

    setStation(item.position);
    setBinId(item.replenishTask.binId ?? "");
    setQCount(skuQuantity);
    setSelectedTaskId(item.id);
    setSelectedDate(item.creationDate ?? "");

    setSelectedOrder({
      ...item,
      isStatic: isStaticTask,
      selectedQty: formattedQty,
      quantity: skuQuantity,
    });

    if (item?.skuId && (!skuDetailsCache.current.has(item.skuId) || !item.productName)) {
      fetchSkuDetails(item.skuId, item.id);
    }

    setTargetItemsData((current: any[]) =>
      current.map((obj: any) => {
        if (obj.id === item.id && obj.orderColor !== "completed") {
          return {
            ...obj,
            orderColor: "progress",
          };
        }

        if (obj.orderColor === "progress" && obj.selectedQty > 0 && obj.id !== item.id) {
          return {
            ...obj,
            orderColor: "hold",
          };
        }

        return obj;
      })
    );

    setIsItemModalVisible(true);
  };

  const addQty = (next: number) => {
    if (!selectedOrder) {
      return;
    }

    const setTargetItemsData =
      selectedOrder?.isStatic === true ? setStaticItemsData : setItemsData;
    const safeNext = Math.max(next, 0);
    const formatted = formatQuantity(safeNext);
    const requiredQty = selectedOrder.quantity ?? 0;
    const hasMetRequirement = requiredQty > 0 && safeNext >= requiredQty;

    setQCount(safeNext);

    setSelectedOrder((current: any) => ({
      ...current,
      selectedQty: formatted,
    }));

    setTargetItemsData((current: any[]) =>
      current.map((obj: any) => {
        if (obj.id === selectedOrder.id) {
          return {
            ...obj,
            selectedQty: formatted,
            orderColor: hasMetRequirement ? "completed" : "progress",
          };
        }

        return obj;
      })
    );
  };

  const removeQty = (next: number) => {
    if (!selectedOrder) {
      return;
    }

    const setTargetItemsData =
      selectedOrder?.isStatic === true ? setStaticItemsData : setItemsData;
    const safeNext = Math.max(next, 0);
    const formatted = formatQuantity(safeNext);
    const requiredQty = selectedOrder.quantity ?? 0;
    const hasMetRequirement = requiredQty > 0 && safeNext >= requiredQty;

    setQCount(safeNext);

    setSelectedOrder((current: any) => ({
      ...current,
      selectedQty: formatted,
    }));

    setTargetItemsData((current: any[]) =>
      current.map((obj: any) => {
        if (obj.id === selectedOrder.id) {
          return {
            ...obj,
            selectedQty: formatted,
            orderColor: hasMetRequirement ? "completed" : "progress",
          };
        }

        return obj;
      })
    );
  };

  const closeItemModal = () => {
    setIsItemModalVisible(false);
    setItemModalLoading(false);
  };

  const REGULAR_SLOT_CARD_HEIGHT = "280px";
  const STATIC_SLOT_CARD_HEIGHT = "180px";
  const STATIC_SLOT_CARD_WIDTH = "140px";

  const emptySlot = (label: string, isStatic: boolean = false) => (
    <div style={{
      height: isStatic ? STATIC_SLOT_CARD_HEIGHT : REGULAR_SLOT_CARD_HEIGHT,
      backgroundColor: "#f5f5f5",
      border: "2px dashed #d9d9d9",
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{ flex: 1 }} />
      <div
        style={{
          padding: "6px 8px",
          backgroundColor: "#e9e9e9",
          borderTop: "1px solid #d3d3d3",
          textAlign: "center",
          fontSize: "12px",
          fontWeight: "bold",
          color: "#666",
          borderBottomLeftRadius: "6px",
          borderBottomRightRadius: "6px",
        }}
      >
        Bin - {formatSlotDisplay(label)}
      </div>
    </div>
  );

  const renderDropSlot = (slot: any) => {
    const stateKey = slot.orderColor || "default";
    const isActive = selectedOrder && selectedOrder.id === slot.id;
    const isCompleted = slot.completedStatus || stateKey === "completed";
    const isStaticSlot = slot?.isStatic === true;
    const slotCardHeight = isStaticSlot
      ? STATIC_SLOT_CARD_HEIGHT
      : REGULAR_SLOT_CARD_HEIGHT;
    const handleSlotClick = () => {
      if (!slot.completedStatus) {
        onHandleOrderItem(slot);
      }
    };

    if (isActive) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: slotCardHeight,
            border: "2px solid #b5b5b5",
            borderRadius: "8px",
            backgroundColor: "#f0f0f0",
            cursor: slot.completedStatus ? "default" : "pointer",
            transition: "all 0.2s ease",
          }}
          onClick={handleSlotClick}
        >
          <div
            style={{
              flex: 1,
              backgroundColor: "#e8e8e8",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          />
          <div
            style={{
              padding: "6px 8px",
              backgroundColor: "#d9d9d9",
              borderTop: "1px solid #c6c6c6",
              textAlign: "center",
              fontSize: "12px",
              fontWeight: "bold",
              color: "#333",
              borderBottomLeftRadius: "6px",
              borderBottomRightRadius: "6px",
            }}
          >
            Bin - {formatSlotDisplay(slot.position) || "- NA -"}
          </div>
        </div>
      );
    }

    const slotState = DROP_SLOT_STATES[stateKey] ?? DROP_SLOT_STATES.default;
    const quantity = Array.isArray(slot?.replenishTask?.binSkus)
      ? slot.replenishTask.binSkus.reduce((sum: number, sku: any) => sum + (Number(sku?.quantity) || 0), 0)
      : 0;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: slotCardHeight,
          border: `2px solid ${slotState.border}`,
          borderRadius: "8px",
          backgroundColor: slotState.bg,
          cursor: slot.completedStatus ? "default" : "pointer",
          transition: "all 0.2s ease",
        }}
        onClick={handleSlotClick}
      >
        <div style={{
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
        }}>
          {isCompleted && <TickIcon />}
        </div>
        <div style={{
          padding: "6px 8px",
          backgroundColor: "#d9d9d9",
          borderTop: "1px solid #c6c6c6",
          textAlign: "center",
          fontSize: "12px",
          fontWeight: "bold",
          color: "#333",
          borderBottomLeftRadius: "6px",
          borderBottomRightRadius: "6px",
        }}>
          Bin - {formatSlotDisplay(slot.position) || "- NA -"}
        </div>
      </div>
    );
  };

  const selectedOrderRequiredQty = selectedOrder?.quantity ?? 0;
  const countedQty = Number.parseInt(selectedOrder?.selectedQty ?? "0", 10) || 0;
  const isQtyMatched = Boolean(selectedOrder) && countedQty === selectedOrderRequiredQty;
  const qtyStateSurface = isQtyMatched ? "rgba(7, 165, 104, 0.12)" : "rgba(231, 216, 77, 0.18)";
  const qtyStateBorder = isQtyMatched ? "rgba(7, 165, 104, 0.35)" : "rgba(231, 216, 77, 0.4)";
  const qtyStateAccent = isQtyMatched ? "rgb(7, 165, 104)" : "rgb(168, 150, 30)";

  return (
    <>
      <Styles.Container className="order-detail">
        <Styles.DropBinsWrapper>
          <Styles.TaskBar>
          <Styles.TaskBarInner>
            {selectedTaskId !== "" && (
              <Styles.Item>
                <span>Task ID:</span>
                <span>{selectedTaskId}</span>
              </Styles.Item>
            )}
          </Styles.TaskBarInner>
          <Styles.TaskBarInner>
            <Styles.Item className="timestamp">
              {selectedDate !== "" && (
                <span>Placed @ {dateTimeFormat(selectedDate)} Ago</span>
              )}
            </Styles.Item>
            <Styles.ItemText countReached={false}>
              <span>Items : </span>
              <span>
                {allQty < 10 ? ("0" + `${allQty}`).slice(-2) : allQty} /
                {totalOrderCount < 10
                  ? ("0" + `${totalOrderCount}`).slice(-2)
                  : totalOrderCount}
              </span>
            </Styles.ItemText>
          </Styles.TaskBarInner>
        </Styles.TaskBar>
          <Styles.PickupContainer state={String(firstRow.length || 0)}>
            {firstRow.length > 0 && (
              <Styles.GridRow columns={firstRow.length || 1}>
                {firstRow.map((val: any) => {
                  const indexItem = getVisibleSlotItem(
                    itemsData.filter((item: any) => item.position === val)
                  );
                  const completedItem = completedSlots[val];
                  return indexItem
                    ? renderDropSlot(indexItem)
                    : completedItem
                      ? renderDropSlot(completedItem)
                      : emptySlot(val);
                })}
              </Styles.GridRow>
            )}
            <Styles.GridRow columns={secondRow.length || 1}>
              {secondRow.map((val: any) => {
                const indexItem = getVisibleSlotItem(
                  itemsData.filter((item: any) => item.position === val)
                );
                const completedItem = completedSlots[val];
                return indexItem
                  ? renderDropSlot(indexItem)
                  : completedItem
                    ? renderDropSlot(completedItem)
                    : emptySlot(val);
              })}
            </Styles.GridRow>
          </Styles.PickupContainer>

          {staticSlots.length > 0 && (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1f5454", marginTop: 12 }}>
                Static Pickwall
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  width: "100%",
                  padding: "0 12px",
                  overflowX: "auto",
                }}
              >
                {staticSlots.map((slot) => {
                  const staticItem = getVisibleSlotItem(
                    staticItemsData.filter((item: any) => item.staticSlotId === slot.id)
                  );
                  const completedItem = completedSlots[slot.position];

                  return (
                    <div
                      key={slot.id}
                      style={{
                        flex: `0 0 ${STATIC_SLOT_CARD_WIDTH}`,
                        width: STATIC_SLOT_CARD_WIDTH,
                        minWidth: STATIC_SLOT_CARD_WIDTH,
                      }}
                    >
                      {staticItem
                        ? renderDropSlot(staticItem)
                        : completedItem
                        ? renderDropSlot(completedItem)
                        : emptySlot(slot.position, true)}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Styles.DropBinsWrapper>

        <Modal
          visible={isItemModalVisible}
          onCancel={closeItemModal}
          footer={null}
          width={840}
        >
          {selectedOrder && (
            <div style={{ display: "flex", flexDirection: "column", gap: 24, width: "100%" }}>
              <div style={{ display: "flex", gap: 24, width: "100%", alignItems: "stretch", minHeight: 260 }}>
                <div style={{ flex: "0 0 300px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 16 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ fontSize: 20, fontWeight: 700, textTransform: "uppercase" }}>{selectedOrder?.productName || selectedOrder?.replenishTask?.skuId || "--"}</div>
                      <div style={{ fontSize: 13, letterSpacing: 1, textTransform: "uppercase", color: "#666" }}>{selectedOrder?.skuId || selectedOrder?.replenishTask?.skuId || "--"}</div>
                      <div style={{ fontSize: 14, color: "#555" }}>{selectedOrder?.productDescription || selectedOrder?.description || ""}</div>
                      <div style={{ fontSize: 13, color: "#666" }}>
                        Batch ID: {resolveBatchId(selectedOrder) || "--"}
                      </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ display: "flex", gap: 12 }}>
                      <div style={{ flex: 1, border: `1px solid ${qtyStateBorder}`, backgroundColor: qtyStateSurface, borderRadius: 12, padding: 16, textAlign: "center", transition: "all 0.2s ease" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Qty Needed</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: qtyStateAccent }}>{formatQuantity(selectedOrder?.quantity || 0)}</div>
                      </div>
                      <div style={{ flex: 1, border: `1px solid ${qtyStateBorder}`, backgroundColor: qtyStateSurface, borderRadius: 12, padding: 16, textAlign: "center", transition: "all 0.2s ease" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Qty Counted</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: qtyStateAccent }}>{selectedOrder?.selectedQty || "00"}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                      <Button
                        shape="circle"
                        disabled={parseInt(selectedOrder?.selectedQty || "0", 10) === 0}
                        onClick={() => removeQty(qCount - 1)}
                        size="large"
                        style={{ width: 56, height: 56, fontSize: 24, display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        -
                      </Button>
                      <div style={{ minWidth: 72, borderRadius: 12, border: `1px solid ${qtyStateBorder}`, backgroundColor: qtyStateSurface, padding: "12px 16px", textAlign: "center", fontSize: 20, fontWeight: 700, color: qtyStateAccent, transition: "all 0.2s ease" }}>
                        {formatQuantity(qCount)}
                      </div>
                      <Button
                        shape="circle"
                        onClick={() => addQty(qCount + 1)}
                        size="large"
                        style={{ width: 56, height: 56, fontSize: 24, display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        +
                      </Button>
                    </div>
                    <Button block disabled={qCount === 0} onClick={() => removeQty(0)}>
                      Make Qty as Zero
                    </Button>
                  </div>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 220, height: 220, borderRadius: 16, border: "1px solid #d9d9d9", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 20, padding: 16 }}>
                    {selectedOrder?.image ? (
                      <img src={selectedOrder.image} alt="product" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    ) : (
                      <ProductPlaceholder style={{ width: "60%", height: "60%" }} />
                    )}
                  </div>
                </div>
                <div style={{ flex: "0 0 180px", display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingRight: 4 }}>
                  <Button
                    type="primary"
                    block
                    disabled={qCount === 0}
                    loading={selectedOrder?.okLoader}
                    onClick={() => onHandleOkItem(selectedOrder?.id || "")}
                    style={{ marginTop: "auto" }}
                  >
                    Done
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal>
        <OkPopup
          visible={isOkPopup}
          isCloseOkPopup={isCloseOkPopup}
          errorMessage={errorMessage}
          isCancel={isCancelOkPopup}
        />
      </Styles.Container>
      <>{contextHolder}</>
    </>
  );
}
